import { assertNever, check } from '../core.ts';
import type { Objectish, TypeGuard } from '../identity.ts';
import {
  isArray,
  isBoolean,
  isNumber,
  isObject,
  isPresent,
  isString,
  recordEntries,
  typeGuard,
} from '../identity.ts';
import { DateTime, parseStandardTime } from '../time.ts';
import { Scalar } from '../units.ts';
import {
  hasEnumValue,
  type AnyTypeDescriptor,
  type Descriptor,
  type StrictPropertyList,
  type Struct,
  type TypeDescriptor,
} from './descriptor.ts';

export type Converter = <T>(d: TypeDescriptor<T>, value: unknown) => T | undefined;

export type ValidationError = {
  field: string;
  value: unknown;
  reason?: string;
};

export function toErrorMessage(ve: ValidationError) {
  return `${ve.field} is ${ve.reason ?? 'invalid'}`;
}

// TODO: Parameterize these processors with a function per key?
// TODO: Accept Partial<ValueType>?
// TODO: ValidationError tied to keys?
function coerceProperty<T>(
  o: Objectish,
  p: TypeDescriptor<T>,
  k: string | number,
  v: unknown,
  partial: boolean,
  fix: boolean,
  converter: Converter | undefined,
): ValidationError[] {
  const isArrayIndex = isNumber(k);
  const depKeyMatch =
    !isArrayIndex && isPresent(p.dk) && (o as Record<string, unknown>)[p.dk] === p.dv;
  if (!isArrayIndex && isPresent(p.dk) && !depKeyMatch) {
    if (fix) {
      delete o[k];
    }
    return [];
  }

  if (converter) {
    check(!isArrayIndex, "Can't convert arrays.");
    o[k] = v = converter(p, v);
  }

  // For validation purposes, treat the empty string as absent.
  const isAbsent = v === undefined || (p.t === 'string' && v === '' && !hasEnumValue(p, v));
  const isRequired = p.req === true || depKeyMatch;
  if (isAbsent && (!isRequired || partial)) {
    return [];
  }

  const defaultError: ValidationError = { field: k.toString(), value: v };
  let result = [defaultError];

  if (isRequired && isAbsent) {
    defaultError.reason = 'required';
    return result;
  }

  switch (p.t) {
    case 'bool':
      if (!isBoolean(v)) {
        defaultError.reason = 'not a boolean';
      } else {
        // It seems aggressive to delete even default values.
        // if (fix && !isRequired && !v) {
        //   delete o[k];
        // }
        return [];
      }
      break;
    case 'number':
      if (!isNumber(v)) {
        defaultError.reason = 'not a number';
      } else if (p.enum && !hasEnumValue(p, v)) {
        defaultError.reason = `not a valid value (${p.enum.map((e) => e.toString()).join(',')})`;
      } else {
        return [];
      }
      break;
    case 'string':
      if (!isString(v)) {
        defaultError.reason = 'not a string';
      } else if (p.enum && !hasEnumValue(p, v)) {
        defaultError.reason = `not a valid value (${p.enum.join(',')})`;
      } else {
        // This is shady. We want to treat required strings to be invalid if empty, but let's not
        // prevent submitting them.
        // if (fix && !isRequired && v === '') {
        //   // Treat the empty string as absent if this is not a required field.
        //   delete o[k];
        // }
        return [];
      }
      break;
    case 'datetime':
      if (v instanceof DateTime) {
        // TODO: Validate subtypes of datetime.
        return [];
      }
      // DateTimes convert to ISO during JSON stringification, but timezone data is lost.
      if (fix && isString(v)) {
        let date: DateTime;
        if (p.st === 'time') {
          date = parseStandardTime(v);
        } else {
          date = DateTime.fromISO(v);
        }
        if (date.isValid) {
          if (p.st === 'date') {
            date = date.toUTC();
          }
          o[k] = date;
          return [];
        }
      }
      defaultError.reason = 'not a date';
      break;
    case 'scalar':
      if (!(v instanceof Scalar)) {
        defaultError.reason = 'not a scalar';
      } else if (v.units.quantity !== p.u.quantity) {
        defaultError.reason = `not a ${p.u.quantity.label} quantity`;
      } else {
        return [];
      }
      break;
    case 'ref':
      // TODO: This is not as rigorous as instanceof DocumentReference. Think about whether these
      // more general descriptors should be overridable in a particular context (e.g. Firestore).
      if (!(isObject(v) && 'path' in v && isString(v.path))) {
        defaultError.reason = 'not a reference';
      } else {
        // if (isString(v) && v === '' && !isRequired) {
        //   // Treat the empty string as absent if this is not a required field.
        //   if (fix) {
        //     delete o[k];
        //   }
        // }
        return [];
      }
      break;
    case 'dict':
      if (!isObject(v)) {
        defaultError.reason = 'not an object';
      } else {
        const errors = recordEntries(v).flatMap(([k, e]) => {
          if (p.enum && !hasEnumValue(p, k)) {
            return { ...defaultError, reason: `not a valid key (${p.enum.join(',')})` };
          }
          return coerceProperty(o, p.el as AnyTypeDescriptor, k, e, partial, fix, converter);
        });
        if (errors.length === 0) {
          return [];
        }
        result = errors.map((error) => ({ ...error, field: `${k}.${error.field}` }));
      }
      break;
    case 'array':
      if (!isArray(v)) {
        defaultError.reason = 'not an array';
      } else {
        const errors = v.flatMap((e, i) =>
          coerceProperty(o, p.el as AnyTypeDescriptor, i, e, partial, fix, converter),
        );
        if (errors.length === 0) {
          return [];
        }
        result = errors.map((error) => ({ ...error, field: `${k}.${error.field}` }));
      }
      break;
    case 'obj':
      if (!isObject(v)) {
        defaultError.reason = 'not an object';
      } else {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const { errors } = coerce(v, p.el, { partial: partial as true, fix, converter });
        if (errors.length === 0) {
          return [];
        }
        result = errors.map((error) => ({ ...error, field: `${k}.${error.field}` }));
      }
      break;
    default:
      assertNever(p);
  }
  return result;
}

export type ValidateOptions = {
  converter?: Converter | undefined;
  partial?: boolean | undefined;
  fix?: boolean | undefined;
};

export type ValidationResult<R> = {
  valid?: R;
  errors: ValidationError[];
};

// TODO: pare only non-required fields.
// TODO: Overload without options to return typeguard?
export function coerce<T extends Struct>(
  v: unknown,
  d: Descriptor<T> | StrictPropertyList<T>,
  options?: ValidateOptions & { partial?: false | undefined },
): ValidationResult<T>;
export function coerce<T extends Struct>(
  v: unknown,
  d: Descriptor<T> | StrictPropertyList<T>,
  options?: ValidateOptions & { partial: true },
): ValidationResult<Partial<T>>;
export function coerce<T extends Struct>(
  v: unknown,
  d: Descriptor<T> | StrictPropertyList<T>,
  options?: ValidateOptions,
): ValidationResult<T> {
  // TODO: Rigorously wrap the input option with a readonly wrapper if fix is false to prevent
  // alteration.
  if (!isObject(v)) {
    return { errors: [{ field: '*', value: undefined, reason: 'No fields present.' }] };
  }
  const partial = options?.partial ?? false;
  const fix = options?.fix ?? true;
  const props = isArray(d) ? d : d.props;
  const result = {
    valid: v,
    errors: props.flatMap((p) =>
      coerceProperty<T[keyof T & string] & {}>(v, p, p.k, v[p.k], partial, fix, options?.converter),
    ),
  } as ValidationResult<T>;
  if (result.errors.length) {
    delete result.valid;
  }
  // console.log('validate', { result });
  return result;
}

export function isValidSchema<T extends Struct>(
  d: Descriptor<T> | StrictPropertyList<T>,
): TypeGuard<T> {
  const f = (v: unknown) => coerce(v, d, { converter: undefined, partial: false, fix: false });
  return typeGuard(
    (v) => isPresent(f(v).valid),
    (v) => f(v).errors.map(toErrorMessage).join(', '),
  );
}
